import { useEffect, useRef, useState } from "react";
import { getBookingMeta, upsertBookingMeta, updateCampGroup } from "../api/groups";
import { listStaysForStudents, updateStay, createStay } from "../api/people";
import { listNsrsForAdult, updateNsr, createNsr } from "../api/people";
import { fmtDate, toSqlDateLocal, toSqlTime, parseSqlDate } from "../utils/dates";

async function runWithConcurrency(items, worker, limit = 4) {
  const results = [];
  let i = 0;

  async function runner() {
    while (i < items.length) {
      const item = items[i++];
      results.push(await worker(item));
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, runner);
  await Promise.all(workers);
  return results;
}

export function useBookingMeta({ group, token }) {
  const [loading, setLoading] = useState(false);
  const [exists, setExists] = useState(false);
  const [meta, setMeta] = useState({
    defaultCampus: group?.campus || "",
    defaultStartDate: null,
    defaultEndDate: null,
    defaultNationality: "",
    defaultRate: "",
    status: "",

    // Read-only, set by the HubSpot deal import (null on hand-made groups). Never
    // sent back — the update path ignores them.
    hubspotDealId: null,
    hubspotDealUrl: null,
    hubspotImportedAt: null,

    inTransferDate: null,
    inDepart: "",
    inArrive: "",
    inDepartureTime: "",
    inArrivalTime: "",
    inFlightId: "",
    inNotes: "",

    outTransferDate: null,
    outDepart: "",
    outArrive: "",
    outDepartureTime: "",
    outArrivalTime: "",
    outFlightId: "",
    outNotes: "",
  });

  // NEW: Keep the last-saved meta from the server (the "OLD" we should match against)
  const lastSavedMetaRef = useRef({
    defaultCampus: group?.campus || "",
    defaultStartDate: null,
    defaultEndDate: null,
    status: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const m = await getBookingMeta(group.id, token);
        if (!mounted) return;
        setExists(true);
        const next = {
          defaultCampus: m.defaultCampus || group?.campus || "",
          defaultStartDate: m.defaultStartDate ? new Date(m.defaultStartDate) : null,
          defaultEndDate: m.defaultEndDate ? new Date(m.defaultEndDate) : null,
          defaultNationality: m.defaultNationality || "",
          defaultRate: (m.defaultRate ?? "") + "",
          status: (m.status ?? ""),

          hubspotDealId: m.hubspotDealId ?? null,
          hubspotDealUrl: m.hubspotDealUrl ?? null,
          hubspotImportedAt: m.hubspotImportedAt ?? null,

          inTransferDate: parseSqlDate(m.inTransferDate),
          inDepart: m.inDepart ?? "",
          inArrive: m.inArrive ?? "",
          inDepartureTime: m.inDepartureTime ?? "",
          inArrivalTime: m.inArrivalTime ?? "",
          inFlightId: m.inFlightId ?? "",
          inNotes: m.inNotes ?? "",

          outTransferDate: parseSqlDate(m.outTransferDate),
          outDepart: m.outDepart ?? "",
          outArrive: m.outArrive ?? "",
          outDepartureTime: m.outDepartureTime ?? "",
          outArrivalTime: m.outArrivalTime ?? "",
          outFlightId: m.outFlightId ?? "",
          outNotes: m.outNotes ?? "",
        };
        // 🔹 UX defaulting (only if not already set)
        if (!next.inTransferDate && next.defaultStartDate) {
          next.inTransferDate = new Date(next.defaultStartDate);
        }

        if (!next.outTransferDate && next.defaultEndDate) {
          next.outTransferDate = new Date(next.defaultEndDate);
        }
        setMeta(next);
        // snapshot what we just loaded as the "old" baseline
        lastSavedMetaRef.current = {
          defaultCampus: next.defaultCampus,
          defaultStartDate: next.defaultStartDate ? fmtDate(next.defaultStartDate) : null,
          defaultEndDate: next.defaultEndDate ? fmtDate(next.defaultEndDate) : null,
          status: next.status ?? "",
        };
      } catch {
        if (!mounted) return;
        setExists(false);
        const next = {
          defaultCampus: group?.campus || "",
          defaultStartDate: null,
          defaultEndDate: null,
          defaultNationality: "",
          defaultRate: "",
          status: "",
        };
        setMeta(next);
        lastSavedMetaRef.current = {
          defaultCampus: next.defaultCampus,
          defaultStartDate: null,
          defaultEndDate: null,
          status: next.status ?? "",
        };
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [group?.id, token]);

  const save = async ({ groupName, notes, studentIdsForStaySync = [], adultIdsForStaySync = [] }) => {
    // OLD values must come from lastSavedMetaRef (not from current meta which already reflects edits)
    const oldCampus = lastSavedMetaRef.current.defaultCampus || null;
    const oldArr = lastSavedMetaRef.current.defaultStartDate || null; // already YYYY-MM-DD
    const oldDep = lastSavedMetaRef.current.defaultEndDate || null;   // already YYYY-MM-DD

    // NEW values from current meta
    const newCampus = meta.defaultCampus || null;
    const newArr = meta.defaultStartDate ? fmtDate(meta.defaultStartDate) : null;
    const newDep = meta.defaultEndDate ? fmtDate(meta.defaultEndDate) : null;

    const normCampus = (v) => (v ?? "").toString().trim().toLowerCase();
    const d10 = (s) => (s ? s.toString().slice(0, 10) : null);

    const stayFieldsChanged =
      normCampus(oldCampus) !== normCampus(newCampus) ||
      d10(oldArr) !== d10(newArr) ||
      d10(oldDep) !== d10(newDep);

    // 1) update campgroup shell
    await updateCampGroup(group.id, {
      groupName: groupName.trim(),
      campus: meta.defaultCampus,
      notes: notes?.trim() || "",
      groupType: "BOOKING",
    }, token);

    // 2) upsert booking meta
    const rateVal = meta.defaultRate === "" || isNaN(Number(meta.defaultRate)) ? 0.0 : Number(meta.defaultRate);
    await upsertBookingMeta(group.id, {
      defaultCampus: meta.defaultCampus,
      defaultStartDate: newArr,
      defaultEndDate: newDep,
      defaultNationality: meta.defaultNationality?.trim() || null,
      defaultRate: rateVal,
      status: meta.status || null,

      inTransferDate: toSqlDateLocal(meta.inTransferDate),
      inDepart: meta.inDepart || null,
      inArrive: meta.inArrive || null,
      inDepartureTime: toSqlTime(meta.inDepartureTime),
      inArrivalTime: toSqlTime(meta.inArrivalTime),
      inFlightId: meta.inFlightId || null,
      inNotes: meta.inNotes || null,

      outTransferDate: toSqlDateLocal(meta.outTransferDate),
      outDepart: meta.outDepart || null,
      outArrive: meta.outArrive || null,
      outDepartureTime: toSqlTime(meta.outDepartureTime),
      outArrivalTime: toSqlTime(meta.outArrivalTime),
      outFlightId: meta.outFlightId || null,
      outNotes: meta.outNotes || null,
    }, token, exists);

  setExists(true);

  // 3) sync member stays
  if (
  stayFieldsChanged &&
  newCampus &&
  newArr &&
  newDep &&
  studentIdsForStaySync.length
) {
    const normCampus = (v) => (v ?? "").toString().trim().toLowerCase();
    const d10 = (s) => (s ? s.toString().slice(0, 10) : null); // input is already "YYYY-MM-DD"
    const oldC = normCampus(oldCampus);
    const oldA = d10(oldArr);
    const oldD = d10(oldDep);
    const newC = normCampus(newCampus);
    const newA = d10(newArr);
    const newD = d10(newDep);

    const staysByStudent = await listStaysForStudents(token, studentIdsForStaySync);
    await runWithConcurrency(studentIdsForStaySync, async (sid) => {
  try {
    const raw = staysByStudent[sid] || [];
    const arr = Array.isArray(raw) ? raw : [];

    const S = arr.map((st) => ({
      id: st.stayId,
      campus: normCampus(st.campus),
      arr: d10(st.arrivalDate),
      dep: d10(st.departureDate),
    }));

    const alreadyNew = S.find((st) => st.campus === newC && st.arr === newA && st.dep === newD);
    if (alreadyNew) return;

    let match = (oldC && oldA && oldD)
      ? S.find((st) => st.campus === oldC && st.arr === oldA && st.dep === oldD)
      : null;

    if (!match && oldA && oldD) match = S.find((st) => st.arr === oldA && st.dep === oldD);
    if (!match && S.length === 1) match = S[0];

    if (match?.id != null) {
      await updateStay(token, sid, match.id, {
        campus: newCampus,
        arrivalDate: newArr,
        departureDate: newDep,
      });
    } else {
      await createStay(token, sid, {
        campus: newCampus,
        arrivalDate: newArr,
        departureDate: newDep,
      });
    }
  } catch (e) {
    console.warn("Stay sync failed for student", sid, e);
  }
}, 4);

  }

  // 3b) sync ADULT member stays (NSRs)
if (
  stayFieldsChanged &&
  newCampus &&
  newArr &&
  newDep &&
  adultIdsForStaySync?.length
) {
  const normCampus = (v) => (v ?? "").toString().trim().toLowerCase();
  const d10 = (s) => (s ? s.toString().slice(0, 10) : null);

  const oldC = normCampus(oldCampus);
  const oldA = d10(oldArr);
  const oldD = d10(oldDep);

  const newC = normCampus(newCampus);
  const newA = d10(newArr);
  const newD = d10(newDep);

  await runWithConcurrency(adultIdsForStaySync, async (aid) => {
  try {
    const raw = await listNsrsForAdult(token, aid);
    const arr = Array.isArray(raw) ? raw : [];

    const S = arr.map((st) => ({
      id: st.id,
      campus: normCampus(st.residentialBase),
      arr: d10(st.arrivaldate),
      dep: d10(st.departuredate),
    }));

    const alreadyNew = S.find((st) => st.campus === newC && st.arr === newA && st.dep === newD);
    if (alreadyNew) return;

    let match = (oldC && oldA && oldD)
      ? S.find((st) => st.campus === oldC && st.arr === oldA && st.dep === oldD)
      : null;

    if (!match && oldA && oldD) match = S.find((st) => st.arr === oldA && st.dep === oldD);
    if (!match && S.length === 1) match = S[0];

    const payload = {
      arrivaldate: newArr,
      departuredate: newDep,
      residentialBase: newCampus,
      overnightResident: true,
      notes: "",
    };

    if (match?.id != null) {
      await updateNsr(token, match.id, payload);
    } else {
      await createNsr(token, aid, payload);
    }
  } catch (e) {
    console.warn("NSR sync failed for adult", aid, e);
  }
}, 2);

}


  // 4) update our "last saved" baseline to the NEW values we just persisted
  lastSavedMetaRef.current = {
  defaultCampus: newCampus,
  defaultStartDate: newArr,
  defaultEndDate: newDep,
  defaultNationality: meta.defaultNationality?.trim() || null,
  defaultRate: rateVal,
  status: meta.status ?? "",

  // IN flight template (persisted form)
  inTransferDate: toSqlDateLocal(meta.inTransferDate),
  inDepart: meta.inDepart || null,
  inArrive: meta.inArrive || null,
  inDepartureTime: toSqlTime(meta.inDepartureTime),
  inArrivalTime: toSqlTime(meta.inArrivalTime),
  inFlightId: meta.inFlightId || null,
  inNotes: meta.inNotes || null,

  // OUT flight template (persisted form)
  outTransferDate: toSqlDateLocal(meta.outTransferDate),
  outDepart: meta.outDepart || null,
  outArrive: meta.outArrive || null,
  outDepartureTime: toSqlTime(meta.outDepartureTime),
  outArrivalTime: toSqlTime(meta.outArrivalTime),
  outFlightId: meta.outFlightId || null,
  outNotes: meta.outNotes || null,
};

};


  return { loading, exists, meta, setMeta, save };
}
